import { useCallback, useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button as Button } from '@factorialco/f0-react'
import { EntityId, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { WorkflowRoleSelectorDocument } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import JobCatalogRoleSelector from './JobCatalogRoleSelector'
import LegacyRoleSelector from './LegacyRoleSelector'
import { StrategySelector } from './StrategySelector'
import {
  StrategyItem,
  StrategySelectorEmployee,
  StrategySelectorLegacyRole,
  StrategySelectorTeam,
  StrategySelectorTreeRoles,
  StrategyType,
} from './utils/interfaces'
import { getStrategyOptions } from './utils/strategyUtils'

interface ApprovalSettingsModalProps {
  isOpen: boolean
  onClose: () => void
  onAddStrategies: (strategies: StrategyItem[]) => void
  availableEmployees?: Array<StrategySelectorEmployee>
  availableTeams?: Array<StrategySelectorTeam>
  availableLegacyRoles?: Array<StrategySelectorLegacyRole>
  availableTreeRoles?: Array<StrategySelectorTreeRoles>
  availableStrategies?: Array<StrategyType>
  isLoading: boolean
  entityName: string
}

export const ApprovalSettingsModal = ({
  isOpen,
  onClose,
  onAddStrategies,
  availableEmployees = [],
  availableTeams = [],
  availableStrategies = [],
  availableLegacyRoles = [],
  availableTreeRoles = [],
  isLoading,
  entityName,
}: ApprovalSettingsModalProps) => {
  const [strategy, setStrategy] = useState<StrategyType | undefined>(undefined)
  const [selectedEmployeesIds, setSelectedEmployeesIds] = useState<EntityId[]>([])
  const [selectedTeamsIds, setSelectedTeamsIds] = useState<EntityId[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(undefined)

  const { company } = useCurrentSession()

  const { data: rolesData } = useQuery(WorkflowRoleSelectorDocument, {
    variables: { companyId: company.id },
  })

  const isLegacyJobCatalog =
    rolesData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'legacy_job_catalog'
    )?.enabled ?? true

  const resetModal = () => {
    setStrategy(undefined)
    setSelectedEmployeesIds([])
    setSelectedTeamsIds([])
    setSelectedRoleId(undefined)
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  const handleSave = () => {
    if (!strategy) {
      return
    }

    const strategyType = getStrategyOptions(strategy).ui_type
    const strategiesToAdd: StrategyItem[] = []

    if (strategyType === 'manual_selected_employees' && selectedEmployeesIds.length > 0) {
      selectedEmployeesIds.forEach((employeeId) => {
        if (typeof employeeId === 'number') {
          strategiesToAdd.push({
            id: `manual_selection_${employeeId}`,
            type: 'manual_selection',
            employeeId: employeeId,
          })
        }
      })
    } else if (strategyType === 'team' && selectedTeamsIds.length > 0) {
      const teamId = selectedTeamsIds[0]
      if (typeof teamId === 'number') {
        strategiesToAdd.push({
          id: `${strategy}_${teamId}_${Date.now()}`,
          type: strategy,
          teamId: teamId,
        })
      }
    } else if (strategyType === 'role' && selectedRoleId) {
      strategiesToAdd.push({
        id: `${strategy}_${selectedRoleId}_${Date.now()}`,
        type: strategy,
        roleId: selectedRoleId,
      })
    } else if (strategyType === 'none') {
      strategiesToAdd.push({
        id: `${strategy}_${Date.now()}`,
        type: strategy,
      })
    }

    if (strategiesToAdd.length > 0) {
      onAddStrategies(strategiesToAdd)
    }

    resetModal()
    onClose()
  }

  // Build selected entities for display
  const selectedEmployeesEntities = useMemo(
    () =>
      availableEmployees
        .filter((emp) => selectedEmployeesIds.includes(emp.id))
        .map((emp) => ({
          id: emp.id,
          name: emp.fullName,
          avatar: buildUrl(emp.avatar?.url),
        })),
    [availableEmployees, selectedEmployeesIds]
  )

  const selectedTeamsEntities = useMemo(
    () =>
      availableTeams
        .filter((team) => selectedTeamsIds.includes(team.id))
        .map((team) => ({
          id: team.id,
          name: team.name,
        })),
    [availableTeams, selectedTeamsIds]
  )

  const strategyType = useMemo(
    () => (strategy ? getStrategyOptions(strategy).ui_type : 'none'),
    [strategy]
  )

  const isSaveDisabled = useMemo(() => {
    if (!strategyType) return true
    if (strategyType === 'none') return false
    if (strategyType === 'manual_selected_employees') return selectedEmployeesIds.length === 0
    if (strategyType === 'team') return selectedTeamsIds.length === 0
    if (strategyType === 'role') return !selectedRoleId
    return false
  }, [strategyType, selectedEmployeesIds, selectedTeamsIds, selectedRoleId])

  const filteredStrategyOptions = useMemo(
    () =>
      Object.values(getStrategyOptions()).filter(({ ui_type, value }) => {
        // Filter out non available strategies
        if (!availableStrategies.map((s) => s.toString()).includes(value)) return false
        // Filter out manual selected employees if no employees are available
        if (ui_type === 'manual_selected_employees' && availableEmployees.length === 0) return false
        // Filter out role if no roles are available
        if (
          ui_type === 'role' &&
          availableLegacyRoles.length === 0 &&
          availableTreeRoles.length === 0
        )
          return false
        // Filter out team if no teams are available
        if (ui_type === 'team' && availableTeams.length === 0) return false
        return true
      }),
    [
      availableStrategies,
      availableEmployees.length,
      availableLegacyRoles.length,
      availableTreeRoles.length,
      availableTeams.length,
    ]
  )

  const handleEmployeeSelect = useCallback(
    (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
      if (selection === null) {
        setSelectedEmployeesIds([])
        return
      }
      setSelectedEmployeesIds(
        Array.isArray(selection) ? selection.map((s) => s.id) : [selection.id]
      )
    },
    []
  )

  const handleTeamSelect = useCallback(
    (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
      if (selection === null) {
        setSelectedTeamsIds([])
        return
      }
      const teamId = Array.isArray(selection) ? selection[0]?.id : selection.id
      setSelectedTeamsIds(teamId ? [teamId] : [])
    },
    []
  )

  if (!isOpen) return null

  return (
    <Modal
      disableFocusTrap
      title={i18n.t('workflows.sidepanel.approval.add_approvers.title_with_entity', {
        entityName,
      })}
      footer={
        <>
          <Button
            label={i18n.t('common.cancel')}
            variant="outline"
            size="md"
            onClick={handleClose}
          />
          <Button
            label={i18n.t('common.save_changes')}
            variant="default"
            size="md"
            onClick={handleSave}
            disabled={isSaveDisabled}
          />
        </>
      }
      onClose={handleClose}
    >
      <Box gap="s16">
        <Box gap="s8" width="full">
          <Text size="200" weight="medium" className="text-f1-foreground-secondary">
            {i18n.t('workflows.sidepanel.approval.fields.add_title_with_entity', {
              entityName,
            })}
          </Text>

          <StrategySelector
            strategyValue={strategy}
            onStrategyChange={setStrategy}
            onEmployeeSelect={handleEmployeeSelect}
            onTeamSelect={handleTeamSelect}
            selectedEmployees={selectedEmployeesEntities}
            selectedTeams={selectedTeamsEntities}
            availableEmployees={availableEmployees}
            availableTeams={availableTeams}
            isLoading={isLoading}
            strategyOptions={filteredStrategyOptions}
          />

          {strategyType === 'role' && isLegacyJobCatalog && (
            <LegacyRoleSelector
              availableRoles={availableLegacyRoles}
              value={selectedRoleId}
              onChange={(id) => setSelectedRoleId(id ?? undefined)}
            />
          )}

          {strategyType === 'role' && !isLegacyJobCatalog && (
            <JobCatalogRoleSelector
              treeRoles={availableTreeRoles}
              value={selectedRoleId}
              onChange={(id) => setSelectedRoleId(id ?? undefined)}
            />
          )}
        </Box>
      </Box>
    </Modal>
  )
}
